import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';
import axios from 'axios';
import { BASE_URL } from '../config/config';

/**
 * AI Video Summarizer
 * Extracts key points and creates timestamped notes from video content
 */
const AIVideoSummarizer = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { logFeatureUsage } = useSubscription();
  const [inputType, setInputType] = useState('transcript');
  // Form state
  const [transcript, setTranscript] = useState('');
  const [videoUrl, setVideoUrl] = useState(''); // For display/reference only
  const [summaryType, setSummaryType] = useState('detailed');

  // UI state
  const [isGenerating, setIsGenerating] = useState(false);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);

  /**
   * Extract video ID from YouTube URL
   */
  const extractYouTubeId = (url) => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
      /youtube\.com\/embed\/([^&\n?#]+)/,
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    
    return null;
  };

  /**
   * Generate video summary
   */
  const handleSummarize = useCallback(async (e) => {
    e.preventDefault();
    
    if (!transcript.trim()) {
      setError('Please paste the video transcript');
      return;
    }

    if (transcript.trim().length < 200) {
      setError('Transcript is too short. Please provide at least 200 characters.');
      return;
    }

    setIsGenerating(true);
    setError('');

    try {
      const response = await axios.post(
        `${BASE_URL}/api/ai-subscription/features/video-summarizer/summarize`,
        {
          user_id: user.id,
          input_type: 'transcript',
          transcript: transcript.trim(),
          summary_type: summaryType,
          video_url: videoUrl.trim() || null
        }
      );

      if (response.data.success) {
        const newSummary = {
          ...response.data.data,
          videoUrl: videoUrl.trim() || null,
          timestamp: new Date()
        };
        
        setSummary(newSummary);
        
        // Add to history
        setHistory(prev => [newSummary, ...prev].slice(0, 5));
        
        // Log feature usage
        await logFeatureUsage('video_summary', {
          summary_type: summaryType,
          transcript_length: transcript.trim().length
        });
      } else {
        setError(response.data.message || 'Failed to generate summary');
      }
    } catch (err) {
      console.error('Video summarization error:', err);
      setError(
        err.response?.data?.message || 
        'Failed to generate summary. Please try again.'
      );
    } finally {
      setIsGenerating(false);
    }
  }, [transcript, videoUrl, summaryType, user, logFeatureUsage]);

  /**
   * Load summary from history
   */
  const loadFromHistory = useCallback((item) => {
    if (item.videoUrl) {
      setVideoUrl(item.videoUrl);
    }
    setSummary(item);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  /**
   * Reset form
   */
  const handleReset = useCallback(() => {
    setVideoUrl('');
    setTranscript('');
    setSummaryType('detailed');
    setSummary(null);
    setError('');
  }, []);

  /**
   * Format summary text
   */
  const formatSummary = (text) => {
    if (!text) return '';
    
    return text.split('\n').map((line, index) => {
      // Headers
      if (line.startsWith('## ')) {
        return (
          <h3 key={index} className="text-xl font-bold text-gray-800 mt-6 mb-3 flex items-center">
            <span className="w-1 h-6 bg-red-500 mr-3 rounded"></span>
            {line.replace('## ', '')}
          </h3>
        );
      }
      
      if (line.startsWith('### ')) {
        return (
          <h4 key={index} className="text-lg font-semibold text-gray-700 mt-4 mb-2">
            {line.replace('### ', '')}
          </h4>
        );
      }
      
      // Timestamps [00:00]
      if (line.includes('[') && line.includes(']')) {
        const formatted = line.replace(/\[(\d+:\d+)\]/g, '<span class="inline-flex items-center px-2 py-1 bg-red-100 text-red-700 text-xs font-mono rounded mr-2">$1</span>');
        return (
          <p key={index} className="mb-3 text-gray-700" dangerouslySetInnerHTML={{ __html: formatted }} />
        );
      }
      
      // Bold text
      if (line.includes('**')) {
        const formatted = line.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-red-700">$1</strong>');
        return (
          <p key={index} className="mb-2 text-gray-700" dangerouslySetInnerHTML={{ __html: formatted }} />
        );
      }
      
      // Bullet points
      if (line.trim().startsWith('- ')) {
        return (
          <li key={index} className="ml-6 mb-2 text-gray-700 flex items-start">
            <span className="text-red-500 mr-2 font-bold">▶</span>
            <span>{line.trim().substring(2)}</span>
          </li>
        );
      }
      
      // Numbered lists
      if (/^\d+\.\s/.test(line.trim())) {
        return (
          <li key={index} className="ml-6 mb-2 text-gray-700 list-decimal">
            {line.trim().replace(/^\d+\.\s/, '')}
          </li>
        );
      }
      
      // Empty lines
      if (line.trim() === '') {
        return <br key={index} />;
      }
      
      // Regular text
      return (
        <p key={index} className="mb-3 text-gray-700 leading-relaxed">
          {line}
        </p>
      );
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50">
      <PublicHeader />
      
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <button
            onClick={() => navigate('/ai/features')}
            className="inline-flex items-center text-red-600 hover:text-red-800 mb-4 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to AI Features
          </button>
          
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            🎥 AI Video Summarizer
          </h1>
          <p className="text-gray-600 text-lg">
            Extract key points and create timestamped notes from educational videos
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Sidebar - Input Form */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-xl p-6 sticky top-4">
              <h2 className="text-xl font-bold text-gray-800 mb-4">
                Video Details
              </h2>

              <form onSubmit={handleSummarize} className="space-y-4">
                {/* How to Get Transcript Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h3 className="text-sm font-semibold text-blue-900 mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    How to Get Transcript from YouTube
                  </h3>
                  <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside">
                    <li>Open video on YouTube</li>
                    <li>Click "..." (More) button below video</li>
                    <li>Click "Show transcript"</li>
                    <li>Copy all text and paste below</li>
                  </ol>
                </div>

                {/* Video URL (Optional - for reference) */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Video URL (Optional - for reference)
                  </label>
                  <input
                    type="url"
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition"
                    disabled={isGenerating}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Optional: Add URL to embed video player
                  </p>
                </div>

                {/* Transcript Input */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Video Transcript *
                  </label>
                  <textarea
                    value={transcript}
                    onChange={(e) => setTranscript(e.target.value)}
                    placeholder="Paste the video transcript or captions here..."
                    rows="10"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition resize-none"
                    disabled={isGenerating}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Minimum 200 characters required
                  </p>
                </div>

                {/* Summary Type */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Summary Type
                  </label>
                  <select
                    value={summaryType}
                    onChange={(e) => setSummaryType(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition"
                    disabled={isGenerating}
                  >
                    <option value="brief">Brief (Key Points Only)</option>
                    <option value="detailed">Detailed (With Explanations)</option>
                    <option value="timestamped">Timestamped Notes</option>
                  </select>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    <p className="text-sm">{error}</p>
                  </div>
                )}

                {/* Buttons */}
                <div className="space-y-2">
                  <button
                    type="submit"
                    disabled={isGenerating || !transcript.trim()}
                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
                      isGenerating || !transcript.trim()
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-red-600 text-white hover:bg-red-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                    }`}
                  >
                    {isGenerating ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Summarizing...
                      </span>
                    ) : (
                      '🎥 Summarize Video'
                    )}
                  </button>

                  {summary && (
                    <button
                      type="button"
                      onClick={handleReset}
                      className="w-full px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                    >
                      New Video
                    </button>
                  )}
                </div>
              </form>

              {/* History */}
              {history.length > 0 && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Recent Summaries</h3>
                  <div className="space-y-2">
                    {history.map((item, index) => (
                      <button
                        key={index}
                        onClick={() => loadFromHistory(item)}
                        className="w-full text-left px-3 py-2 bg-gray-50 hover:bg-red-50 rounded-lg transition-colors text-sm"
                      >
                        <p className="font-medium text-gray-800 truncate">
                          {item.videoUrl ? '🎥 Video URL' : '📄 Transcript'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(item.timestamp).toLocaleTimeString()}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Main Content - Summary Display */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              {!summary && !isGenerating && (
                <div className="text-center py-16">
                  <div className="text-7xl mb-6">🎥</div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">
                    Save Time with AI-Powered Summaries
                  </h3>
                  <p className="text-gray-600 text-lg mb-6">
                    Get the key insights from any video in seconds
                  </p>
                  <div className="grid md:grid-cols-3 gap-4 max-w-2xl mx-auto">
                    <div className="bg-red-50 p-4 rounded-lg">
                      <div className="text-3xl mb-2">📝</div>
                      <p className="text-sm font-semibold">Key Points</p>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <div className="text-3xl mb-2">⏰</div>
                      <p className="text-sm font-semibold">Timestamps</p>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <div className="text-3xl mb-2">💡</div>
                      <p className="text-sm font-semibold">Insights</p>
                    </div>
                  </div>
                </div>
              )}

              {isGenerating && (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-red-600 mb-4"></div>
                  <p className="text-gray-600 text-lg">Analyzing video content...</p>
                  <p className="text-gray-400 text-sm mt-2">Extracting key insights</p>
                </div>
              )}

              {summary && (
                <div>
                  {/* Video Info */}
                  {summary.videoUrl && extractYouTubeId(summary.videoUrl) && (
                    <div className="mb-6">
                      <div className="aspect-video w-full rounded-lg overflow-hidden bg-gray-100">
                        <iframe
                          width="100%"
                          height="100%"
                          src={`https://www.youtube.com/embed/${extractYouTubeId(summary.videoUrl)}`}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title="Video"
                        ></iframe>
                      </div>
                    </div>
                  )}

                  {/* Summary Header */}
                  <div className="mb-6 pb-4 border-b-2 border-red-100">
                    <div className="flex items-center justify-between">
                      <h2 className="text-2xl font-bold text-gray-800">
                        Video Summary
                      </h2>
                      <span className="px-3 py-1 bg-red-100 text-red-700 text-sm font-medium rounded-full capitalize">
                        {summary.summary_type || summaryType}
                      </span>
                    </div>
                  </div>

                  {/* Summary Content */}
                  <div className="prose max-w-none">
                    {formatSummary(summary.summary || summary.summary_text)}
                  </div>

                  {/* Metadata */}
                  <div className="mt-8 pt-6 border-t border-gray-200 flex items-center justify-between text-sm text-gray-500">
                    <div>
                      {summary.generated_at && (
                        <p>Generated on {new Date(summary.generated_at).toLocaleString()}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-xs">Powered by Gemini AI</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tips Section */}
        <div className="mt-8 bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-6 md:p-8">
          <h3 className="text-xl font-bold text-gray-800 mb-4">🎬 Tips for Best Results</h3>
          <div className="grid md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="text-2xl mb-2">🎓</div>
              <h4 className="font-semibold text-gray-800 mb-1">Educational Content</h4>
              <p className="text-sm text-gray-600">
                Works best with lectures, tutorials, and educational videos
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="text-2xl mb-2">📋</div>
              <h4 className="font-semibold text-gray-800 mb-1">Use Transcripts</h4>
              <p className="text-sm text-gray-600">
                For better accuracy, use the transcript option when available
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="text-2xl mb-2">⏰</div>
              <h4 className="font-semibold text-gray-800 mb-1">Timestamps</h4>
              <p className="text-sm text-gray-600">
                Choose "Timestamped" for time-marked key points
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="text-2xl mb-2">💾</div>
              <h4 className="font-semibold text-gray-800 mb-1">Save Notes</h4>
              <p className="text-sm text-gray-600">
                Copy summaries to your notes for future reference
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AIVideoSummarizer;
